import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useFirebaseAuthReady } from '../../hooks/useFirebaseAuthReady';
import type { InboxMessage, UserInfo } from './models';
import {
  subscribeInbox, subscribeSent, subscribeUnreadCount,
  markAsRead, sendMessage, uploadImage, uploadFile, fetchUsers,
  extractClipboardImage,
} from './services';
import type { SendMessageRequest } from './models';

/* ═══════════════════ useInbox ═══════════════════ */

export function useInbox() {
  const { user } = useAuth();
  const { ready: fbReady, uid: fbUid } = useFirebaseAuthReady();
  const [messages, setMessages] = useState<InboxMessage[]>([]);
  const [sentMessages, setSentMessages] = useState<InboxMessage[]>([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState<'inbox' | 'sent' | 'hidden'>('inbox');

  useEffect(() => {
    if (!user?.userId) return;
    if (!fbReady || !fbUid) return; // wait for Firebase Auth before subscribing

    setLoading(true);
    const unsub1 = subscribeInbox(user.userId, msgs => {
      setMessages(msgs);
      setLoading(false);
    });
    const unsub2 = subscribeSent(user.userId, msgs => {
      setSentMessages(msgs);
    });

    return () => { unsub1(); unsub2(); };
  }, [user?.userId, fbReady, fbUid]);

  const handleMarkAsRead = useCallback(async (msgId: string) => {
    if (!user?.userId) return;
    await markAsRead(user.userId, msgId);
  }, [user?.userId]);

  return { messages, sentMessages, loading, tab, setTab, markAsRead: handleMarkAsRead };
}

/* ═══════════════════ useUnreadCount ═══════════════════ */

export function useUnreadCount() {
  const { user } = useAuth();
  const { ready: fbReady, uid: fbUid } = useFirebaseAuthReady();
  const [count, setCount] = useState(0);

  useEffect(() => {
    if (!user?.userId) return;
    if (!fbReady || !fbUid) return;
    const unsub = subscribeUnreadCount(user.userId, setCount);
    return unsub;
  }, [user?.userId, fbReady, fbUid]);

  return count;
}

/* ═══════════════════ useCompose ═══════════════════ */

export function useCompose() {
  const [open, setOpen] = useState(false);
  const [users, setUsers] = useState<UserInfo[]>([]);
  const [sending, setSending] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [images, setImages] = useState<{ url: string; fileName: string; fileSize: number }[]>([]);
  const [attachments, setAttachments] = useState<{ url: string; fileName: string; fileSize: number }[]>([]);

  // Reply context
  const [replyTo, setReplyTo] = useState<InboxMessage | null>(null);

  useEffect(() => {
    fetchUsers().then(setUsers).catch(() => {});
  }, []);

  const openCompose = useCallback((reply?: InboxMessage) => {
    if (reply) {
      setReplyTo(reply);
    } else {
      setReplyTo(null);
    }
    setImages([]);
    setAttachments([]);
    setOpen(true);
  }, []);

  const closeCompose = useCallback(() => {
    setOpen(false);
    setReplyTo(null);
    setImages([]);
    setAttachments([]);
  }, []);

  const handleUploadImage = useCallback(async (file: File) => {
    setUploading(true);
    try {
      const result = await uploadImage(file);
      setImages(prev => [...prev, { url: result.url, fileName: result.fileName, fileSize: result.fileSize }]);
      return result.url;
    } finally {
      setUploading(false);
    }
  }, []);

  const handlePaste = useCallback(async (e: ClipboardEvent) => {
    const file = extractClipboardImage(e);
    if (file) {
      e.preventDefault();
      await handleUploadImage(file);
    }
  }, [handleUploadImage]);

  const handleUploadFile = useCallback(async (file: File) => {
    setUploading(true);
    try {
      const result = await uploadFile(file);
      setAttachments(prev => [...prev, { url: result.url, fileName: result.fileName, fileSize: result.fileSize }]);
    } finally {
      setUploading(false);
    }
  }, []);

  const removeAttachment = useCallback((url: string) => {
    setAttachments(prev => prev.filter(a => a.url !== url));
  }, []);

  const handleSend = useCallback(async (req: SendMessageRequest) => {
    setSending(true);
    try {
      await sendMessage({ ...req, images, attachments });
      closeCompose();
    } finally {
      setSending(false);
    }
  }, [images, attachments, closeCompose]);

  const removeImage = useCallback((url: string) => {
    setImages(prev => prev.filter(i => i.url !== url));
  }, []);

  return {
    open, users, sending, uploading, images, attachments, replyTo,
    openCompose, closeCompose,
    handleUploadImage, handleUploadFile, handlePaste, handleSend, removeImage, removeAttachment,
  };
}
